import { ImageConfig } from 'konva/lib/shapes/Image';
import React, { useEffect, useMemo, useRef } from 'react';
import { Image } from 'react-konva';
import useImage from 'use-image';

import { Maybe } from 'types/graphql-api.generated';

import { useKonvaImagePromisesContext } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import { IMAGE_EMPTY_PHOTO_PORTRAIT } from 'common/configuration/constants';
import { getImageUrl } from 'common/tools/network/url';

type KonvaImageProps = Omit<ImageConfig, 'image'> & {
  isLocal?: boolean;
  path?: Maybe<string>;
};

const KonvaImage = ({
  height,
  isLocal,
  path,
  width,
  ...props
}: KonvaImageProps) => {
  const { getPromiseResolver, removePromise } = useKonvaImagePromisesContext();

  const { promise, resolve, reject } = useMemo(
    () => getPromiseResolver(path ?? IMAGE_EMPTY_PHOTO_PORTRAIT),
    [getPromiseResolver, path]
  );

  const imgSrc = useMemo(
    () =>
      isLocal && path
        ? path
        : getImageUrl({
            src: path ?? IMAGE_EMPTY_PHOTO_PORTRAIT,
            format: {
              cw: Math.round(width),
              ch: Math.round(height),
              grow: true
            }
          }),
    [height, isLocal, path, width]
  );

  // Resolving "Tainted canvases may not be exported" with Konva
  //
  // Why do we have that insecure error?
  // That is a CORS error. For security reasons, a browser can mark a canvas as tainted when you load images from another domain.
  // In that case, the browser blocks canvas exporting into dataURL or imageData (that is what we are doing on export or when filters are used).
  //
  // How to fix CORS issue?
  // First, you may try to set the crossOrigin = Anonymous attribute of the loading image.
  // This approach will work only if requested domain has an Access-Control-Allow-Origin headers that allow shared requests.
  //
  // https://konvajs.org/docs/posts/Tainted_Canvas.html
  const [image, status] = useImage(imgSrc, 'anonymous');
  const loaded = useRef(false);

  useEffect(() => {
    if (!loaded.current && status !== 'loading') {
      loaded.current = true;
      if (status === 'loaded') {
        resolve?.();
      } else {
        reject?.();
      }
    }
    return () => {
      loaded.current = false;
    };
  }, [status, resolve, reject, image]);

  // Cleanup: remove the promise associated with dismantling
  useEffect(
    () => () => {
      removePromise(promise); // Delete promise if never resolved
    },
    [removePromise, promise]
  );

  return <Image width={width} height={height} image={image} {...props} />;
};

export default KonvaImage;
